/*!
 * Copyright © 2018-2020. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { isElementNull } from '../../../../utils';
import { IGeofencePlot, IHotspotPlot, LatLngBounds } from '../../../core/models';
import { PlacesHttpService } from '../../../core/services/places-http/places-http.service';
import { ChangeMapBounds, TriggerEditPlace } from '../../../core/store/actions';
import { getShowStartingLocation } from '../../../core/store/selectors/maps.selectors';
import { IAppState } from '../../../core/store/state';

@Injectable()
export class PlaceByQueryParamResolver implements Resolve<boolean> {
  constructor(
    private _placeService: PlacesHttpService,
    private readonly _store: Store<IAppState>,
    private _router: Router
  ) {}

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const geofenceId = +route.queryParams['geofenceId'];
    const suggestedGeofenceId = +route.queryParams['suggestedGeofenceId'];

    const plotDetails$ = this._getPlotDetails(geofenceId, suggestedGeofenceId);
    const showStartingLocation$ = this._store.pipe(select(getShowStartingLocation));

    return plotDetails$.pipe(
      switchMap(plot => {
        if (!plot || plot.id === 0) {
          return throwError(() => new Error('Invalid plot data'));
        }
        return showStartingLocation$.pipe(
          filter(item => !isElementNull(item)),
          take(1),
          switchMap(() => {
            this._store.dispatch(new ChangeMapBounds(LatLngBounds.toLatLngs(LatLngBounds.allWorld())));
            this._store.dispatch(new TriggerEditPlace({ id: plot.id, type: plot.type }));
            return of(true);
          })
        );
      }),
      catchError(() => {
        this._router.navigate(['/']);
        return of(false);
      })
    );
  }

  private _getPlotDetails(geofenceId: number, suggestedGeofenceId: number): Observable<IGeofencePlot | IHotspotPlot> {
    if (geofenceId) {
      return this._placeService.getGeofencePlotDetails(geofenceId);
    } else if (suggestedGeofenceId) {
      return this._placeService.getHotspotPlotDetails(suggestedGeofenceId);
    }
    return of(undefined);
  }
}
